// packages/agent-kit/src/useConversations.ts
import useSWRInfinite from 'swr/infinite';
import { conversationService, isApiError } from '@dopomogai/api-client';
import type { ConversationSummary, UUID, PaginatedResponse } from '@dopomogai/types';
import { AgentKitError } from '../types';

interface ConversationOptions {
  limit?: number;
  searchQuery?: string;
}

const getKey = (agentId: UUID | null, options: ConversationOptions) => {
  return (pageIndex: number, previousPageData: PaginatedResponse<ConversationSummary> | null) => {
    if (!agentId) return null;
    if (previousPageData && previousPageData.items.length === 0) return null;

    const { limit = 20, searchQuery = '' } = options;
    const offset = pageIndex * limit;

    return ['conversations', agentId, limit, offset, searchQuery];
  };
};

const conversationsFetcher = async (
  [, agentId, limit, offset, searchQuery]: [string, UUID, number, number, string]
): Promise<PaginatedResponse<ConversationSummary>> => {
  const response = await conversationService.listConversations(
    agentId,
    limit,
    offset,
    searchQuery || null,
    'updated_at',
    'desc'
  );

  if (isApiError(response)) {
    throw new AgentKitError(response.message, response);
  }
  
  return response;
};

export const useConversations = (agentId: UUID | null, options: ConversationOptions = {}) => {
  const { limit = 20 } = options;

  const {
    data,
    error,
    mutate,
    size,
    setSize,
    isValidating,
  } = useSWRInfinite<PaginatedResponse<ConversationSummary>, AgentKitError>(
    getKey(agentId, options),
    conversationsFetcher,
    {
      // CRITICAL ANTI-DDOS SETTINGS
      revalidateOnFocus: false,        // Don't refetch on window focus
      revalidateFirstPage: false,      // Don't refetch page 1 when loading page 2
      dedupingInterval: 5000,          // Throttle identical requests to 5s
      shouldRetryOnError: false,       // Stop infinite retry loops on 500s
    }
  );

  const isLoadingInitialData = !data && !error;
  const isLoading = agentId ? isLoadingInitialData : false;
  const conversations = data ? data.flatMap(page => page.items) : [];
  const totalConversations = data?.[0]?.total ?? 0;
  
  const hasMore = agentId ? (!isLoadingInitialData && conversations.length < totalConversations) : false;

  const loadMore = () => {
    if (hasMore && !isValidating) {
      setSize(size + 1);
    }
  };

  return {
    conversations,
    totalConversations,
    error,
    mutate,
    pageCount: size,
    setPageCount: setSize,
    loadMore,
    hasMore,
    isLoading,
    isValidating,
  };
};